// ============================================================================
// HR PIN MAIN CONTENT - Updated with Checkout Requests + Anomaly Detector
// Path: frontend/src/components/hr-pin/HrPinMainContent.tsx
// ============================================================================

import React, { Suspense, useMemo } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { AuthUser } from '../../services/auth.service';
import HrDashboard from './pages/HrDashboard';
import styles from './HrPinMainContent.module.css';

// ============================================================================
// LAZY LOADED PAGES
// ============================================================================

const HrAttendancePage = React.lazy(() => 
  import('./pages/HrAttendancePage')
);

const HRPayrollReportPage = React.lazy(() => 
  import('../dashboard/pages/HRPayrollReportPage')
);

const OvertimeReportsPage = React.lazy(() => 
  import('../dashboard/pages/OvertimeReportsPage')
);

const HrLeaveApprovalPage = React.lazy(() => 
  import('../dashboard/pages/HrLeaveApprovalPage')
);

const SalarySlipRequestsPage = React.lazy(() =>
  import('./pages/SalarySlipRequestsPage')
);

const CheckoutRequestsPage = React.lazy(() => 
  import('./pages/CheckoutRequestsPage')
);

// 🆕 Anomaly Detector Page
const AttendanceAnomalyDetectorPage = React.lazy(() => 
  import('../dashboard/pages/AttendanceAnomalyDetectorPage')
);

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface HrPinMainContentProps {
  activeTab: string;
  user: AuthUser;
  onNavigate: (tabId: string) => void;
  isTransitioning: boolean;
}

// ============================================================================
// LOADING COMPONENT
// ============================================================================

const PageLoader: React.FC = () => (
  <div className={styles.loaderContainer}>
    <Spin 
      indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
      tip="Loading..."
    />
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HrPinMainContent: React.FC<HrPinMainContentProps> = ({
  activeTab,
  user,
  onNavigate,
  isTransitioning
}) => {
  const content = useMemo(() => {
    switch (activeTab) {
      case 'attendance':
        return (
          <Suspense fallback={<PageLoader />}>
            <HrAttendancePage onNavigate={onNavigate} />
          </Suspense>
        );

      case 'hr-payroll-report':
        return (
          <Suspense fallback={<PageLoader />}>
            <HRPayrollReportPage 
              onBack={() => onNavigate('dashboard')}
              user={user}
              onNavigate={onNavigate}
            />
          </Suspense>
        );

      case 'overtime-reports':
        return (
          <Suspense fallback={<PageLoader />}>
            <OvertimeReportsPage />
          </Suspense>
        );

      case 'hr-leave-approvals':
        return (
          <Suspense fallback={<PageLoader />}>
            <HrLeaveApprovalPage 
              hrManagerId={user.uid}
              hrManagerName={user.employeeName || user.name || 'HR Manager'}
            />
          </Suspense>
        );

      case 'checkout-requests':
        return (
          <Suspense fallback={<PageLoader />}>
            <CheckoutRequestsPage
              hrManagerId={user.uid}
              hrManagerName={user.employeeName || user.name || 'HR Manager'}
            />
          </Suspense>
        );

      // 🆕 Anomaly Detector
      case 'attendance-anomaly-detector':
        return (
          <Suspense fallback={<PageLoader />}>
            <AttendanceAnomalyDetectorPage
              onBack={() => onNavigate('dashboard')}
              onNavigate={onNavigate}
              user={user}
            />
          </Suspense>
        );


        case 'salary-slip-requests':
       return (
         <Suspense fallback={<PageLoader />}>
           <SalarySlipRequestsPage
             hrManagerId={user.uid}
             hrManagerName={user.employeeName || user.name || 'HR Manager'}
           />
         </Suspense>
      );

      case 'dashboard':
      default:
        return <HrDashboard />;
    }
  }, [activeTab, user, onNavigate]);

  return (
    <div className={`${styles.mainContent} ${isTransitioning ? styles.transitioning : ''}`}>
      {content}
    </div>
  );
};

export default HrPinMainContent;